import { useEffect, useCallback, useRef } from "react";

import { shallowEqual } from "~/utils/helpers";

import { useMountEffect } from "./useMountEffect";
import { useRerender } from "./useRerender";

import type { DirectionProp } from "~/components/components";

export interface ScrollPosition {
  atStart: boolean;
  atEnd: boolean;
}

// Extra padding to account for rounding errors
const PAD = 1;

export function useScrollPosition<T extends Element>(
  direction: DirectionProp = "horizontal",
): [React.MutableRefObject<T | null>, ScrollPosition] {
  const rerender = useRerender();

  const position = useRef<ScrollPosition>({ atStart: true, atEnd: false });

  const ref = useRef<T | null>(null);

  const element = ref.current;

  const isHorizontal = direction === "horizontal";

  const measure = useCallback(() => {
    if (!element) {
      position.current = {
        atStart: true,
        atEnd: true,
      };

      return;
    }

    const value = isHorizontal ? element.scrollLeft : element.scrollTop;

    const atStart = value <= 0;

    const atEnd = isHorizontal
      ? Math.ceil(element.clientWidth + value + PAD) >= element.scrollWidth
      : Math.ceil(element.clientHeight + value + PAD) >= element.scrollHeight;

    position.current = {
      atStart,
      atEnd,
    };

    return;
  }, [element, isHorizontal]);

  const handleEvent = useCallback(() => {
    let requestRunning: number | null = null;

    if (requestRunning === null) {
      requestRunning = globalThis.requestAnimationFrame(() => {
        const prevPosition = position.current;

        measure();

        // Only rerender if the measurements have changed
        if (!shallowEqual(prevPosition, position.current)) {
          rerender();
        }

        requestRunning = null;
      });
    }
  }, [measure, rerender]);

  useEffect(() => {
    if (!element) return;

    const observer = new ResizeObserver(() => {
      measure();
      rerender();
    });

    observer.observe(element);
    element?.addEventListener("scroll", handleEvent);

    return () => {
      observer.unobserve(element);
      observer.disconnect();
      element?.removeEventListener("scroll", handleEvent);
    };
  }, [element, handleEvent, measure, ref, rerender]);

  useMountEffect(() => {
    measure();
    rerender();
  });

  return [ref, position.current];
}
